import { useId } from "react"
import { useDropzone } from "react-dropzone"

import { ImageIcon, LayersIcon, UploadCloudIcon, WeightIcon } from "./icons"
import { CircleAlertIcon } from "../../shared/components"
import { ACCEPT, MAX_FILE_MB } from "../config"

interface Props {
  error: string | null
  isLoading: boolean
  onFiles: (files: File[]) => void
}

export const Dropzone = ({ error, isLoading, onFiles }: Props) => {
  const headingId = useId()
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    noPaste: true,
    onDrop: (accepted, rejected) => onFiles([...accepted, ...rejected.map(({ file }) => file)]),
  })

  return (
    <div
      {...getRootProps({
        role: "button",
        "aria-labelledby": headingId,
        className: `flex cursor-pointer flex-col items-center justify-center gap-4 rounded-3xl border-2 border-dashed bg-base-200 bg-radial-[at_50%_0%] from-primary/15 to-transparent to-70% px-6 py-12 text-center transition sm:py-18 ${
          isDragActive
            ? "border-solid border-primary ring-6 ring-primary/15"
            : "border-base-content/20 outline-none hover:border-primary/60 focus-visible:border-primary/60"
        } ${isLoading ? "opacity-60" : ""}`,
      })}
    >
      <input {...getInputProps({ accept: ACCEPT, "aria-label": "Upload an image to convert" })} />
      <span className="mb-2 grid size-17 place-items-center rounded-3xl bg-primary/15 text-primary ring-10 ring-primary/8">
        <UploadCloudIcon className="size-7.5" />
      </span>
      <h2 id={headingId} className="text-[clamp(1.4rem,2.6vw,1.9rem)] leading-tight font-bold">
        Drop an image or GIF here
      </h2>
      <p className="text-muted-content">
        or <span className="link font-bold link-primary">browse your files</span>
      </p>
      <p className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-muted-content">
        <span className="inline-flex items-center gap-1.5">
          <ImageIcon className="size-3.5 text-primary" />
          PNG · JPG · WEBP · SVG · GIF
        </span>
        <span className="inline-flex items-center gap-1.5">
          <WeightIcon className="size-3.5 text-primary" />
          up to {MAX_FILE_MB} MB
        </span>
        <span className="inline-flex items-center gap-1.5">
          <LayersIcon className="size-3.5 text-primary" />
          several PNGs = animation
        </span>
      </p>
      {error && (
        <p
          role="alert"
          className="inline-flex items-center gap-2 rounded-full bg-error/15 px-4 py-2 text-sm font-semibold"
        >
          <CircleAlertIcon className="size-4 shrink-0 text-error" />
          {error}
        </p>
      )}
    </div>
  )
}
